import React, { useState } from "react";

import { Add } from "@mui/icons-material";
import { Box, Button, Typography } from "@mui/material";
import { formatISO } from "date-fns";
import { FormProvider, useFieldArray, useFormContext } from "react-hook-form";
import { makeStyles, withStyles } from "tss-react/mui";

import { RuleKind } from "@/gql/graphql";

import OverrideDateDialog from "./OverrideDateDialog";
import OverrideDateListItem from "./OverrideDateListItem";
import { DateRule, FormValues } from "../SchedulerPage";

const useStyles = makeStyles()((theme) => ({
  root: {
    width: "100%",
  },
}));

const StyledButton = withStyles(Button, {
  root: {
    height: 32,
  },
});

type Props = {
  onRemove: (overrideIndex: number) => void;
};

const OverrideDatePanel: React.FC<Props> = ({ onRemove }) => {
  const { classes } = useStyles();
  const [overrideDatePanelVisibility, setOverrideDatePanelVisibility] =
    useState<boolean>(false);
  const formMethods = useFormContext<FormValues>();
  const { control, getValues } = formMethods;
  const { fields, append, update, remove } = useFieldArray<FormValues>({
    control: control,
    name: "dateRules",
  });
  const overrideDates: DateRule[] = getValues("dateRules");
  function renderOverrides() {
    if (overrideDates.length > 0) {
      return overrideDates.map((e, index) => {
        if (e._destroy) {
          return null;
        }
        if (e.intervals.length > 0) {
          return (
            <OverrideDateListItem
              onRemove={onRemove}
              overrideRuleIndex={index}
              key={index}
              override={e}
            />
          );
        }
      });
    } else {
      return displayNoOverride();
    }
  }

  function handleDateUpdate(date: Date | null) {
    update(fields.length - 1, {
      ...fields[fields.length - 1],
      date: formatISO(date ?? new Date(), {
        representation: "date",
      }),
    });
  }

  function displayNoOverride() {
    return (
      <Typography variant="body1" textAlign="center" color="GrayText">
        You have no override dates yet. Add dates to override in your weekly
        hours.
      </Typography>
    );
  }

  return (
    <FormProvider {...formMethods}>
      <div className={classes.root}>
        <Box display="flex" justifyContent="space-between">
          <Typography variant="body1" color="black" fontWeight="700">
            Add override dates
          </Typography>
          <StyledButton
            variant="outlined"
            startIcon={<Add />}
            onClick={() => {
              setOverrideDatePanelVisibility(true);
              //immediately add a date rule in the form context
              //so that we can apply validations.
              const ruleToAdd = {
                id: "",
                ruleType: RuleKind.Date,
                intervals: [],
                date: formatISO(new Date(), {
                  representation: "date",
                }),
                _destroy: false,
              };
              append(ruleToAdd);
            }}
          >
            Add
          </StyledButton>
        </Box>
        <Box display="flex" flexDirection={"column"}>
          {renderOverrides()}
        </Box>
        <OverrideDateDialog
          visible={overrideDatePanelVisibility}
          onClose={() => setOverrideDatePanelVisibility(false)}
          handleDateUpdate={handleDateUpdate}
          ruleIndex={fields.length - 1}
          onRemove={remove}
        />
      </div>
    </FormProvider>
  );
};

export default OverrideDatePanel;
